import { ref, computed } from 'vue'
import { useSuperStore } from '@/store/pinia/SuperStore'

export interface SuperGuaranteeData {
  minSuperGuarantee: number | null
  maxSuperContributions: number | null
  maxSalaryPerAnnumForSuperGuarantee: number | null
  maxContributionPerAnnumForSuperGuarantee: number | null
}

export interface SuperContributionRates {
  minRate: number
  maxRate: number
}

// Constants for validation limits
export const MAX_SUPER_FUND_LIMIT = 5_000_000
export const MAX_GROSS_ANNUAL_SALARY_LIMIT = 1_000_000

// Utility functions for Vuex stores (without composable wrapper)
export const getMinSuperContributionRateUtil = (): number | null => {
  const superStore = useSuperStore()
  return superStore.getSuperGuaranteeData?.minSuperGuarantee || null
}

export const getSuperContributionThreshHoldMinSalaryUtil = (): number => {
  return 450
}

export const useRetirementCalculator = () => {
  const superStore = useSuperStore()

  const superGuaranteeData = computed(() => superStore.getSuperGuaranteeData)

  /**
   * Gets the minimum super contribution rate (super guarantee rate)
   */
  const getMinSuperContributionRate = (): number | null => {
    return superGuaranteeData.value?.minSuperGuarantee || null
  }

  /**
   * Gets the super contribution threshold minimum salary
   */
  const getSuperContributionThreshHoldMinSalary = (): number => {
    return 450
  }

  /**
   * Calculates super contribution rates based on salary
   */
  const getSuperContributionRatesBySalary = (grossAnnualSalary: number): SuperContributionRates | null => {
    const minRate = getMinSuperContributionRate()
    if (minRate === null) {
      return null
    }
    
    let maxRate = 25

    if (grossAnnualSalary > 0 && superGuaranteeData.value) {
      const maxSalaryForSG = superGuaranteeData.value.maxSalaryPerAnnumForSuperGuarantee || 66220
      const maxContributions = superGuaranteeData.value.maxSuperContributions || 29130

      if (grossAnnualSalary >= maxSalaryForSG) {
        maxRate = Math.round((maxContributions / grossAnnualSalary) * 100)
      }
    }

    return {
      minRate,
      maxRate: Math.max(maxRate, minRate)
    }
  }

  /**
   * Gets maximum user super monthly contribution
   */
  const getMaximumUserSuperMonthlyContribution = (): number => {
    if (!superGuaranteeData.value?.maxSuperContributions) {
      return 2427.5 // Default fallback
    }
    return superGuaranteeData.value.maxSuperContributions / 12
  }

  /**
   * Gets maximum user super contribution per annum
   */
  const getMaximumUserSuperContributionPerAnnum = (): number => {
    return superGuaranteeData.value?.maxSuperContributions || 29130
  }

  /**
   * Gets maximum salary per annum for super guarantee
   */
  const getMaxSalaryPerAnnumForSuperGuarantee = (): number => {
    return superGuaranteeData.value?.maxSalaryPerAnnumForSuperGuarantee || 66220
  }

  /**
   * Gets maximum contribution per annum for super guarantee
   */
  const getMaxContributionPerAnnumForSuperGuarantee = (): number => {
    return superGuaranteeData.value?.maxContributionPerAnnumForSuperGuarantee || 7225
  }

  return {
    getMinSuperContributionRate,
    getSuperContributionThreshHoldMinSalary,
    getSuperContributionRatesBySalary,
    getMaximumUserSuperMonthlyContribution,
    getMaximumUserSuperContributionPerAnnum,
    getMaxSalaryPerAnnumForSuperGuarantee,
    getMaxContributionPerAnnumForSuperGuarantee,
    superGuaranteeData
  }
}